import React, { useState, useEffect, useRef } from "react";
import classes from "./AddCompany.module.css";
import axios from "axios";
import Swal from "sweetalert2";
import { BASE_URL, getAuthHeaders } from "../../Components/API/Api";
import HeaderNav from "../../Components/Dahboard Header/HeaderNav/HeaderNav";
import { useNavigate } from "react-router-dom";

const formatErrorMessage = (message, fallback) => {
  if (typeof message === "string" && message.trim()) return message;
  if (Array.isArray(message)) {
    return message.map((item) => formatErrorMessage(item, "")).filter(Boolean).join("\n") || fallback;
  }
  if (message && typeof message === "object") {
    const fieldLabels = { bvn: "BVN" };
    const lines = Object.entries(message).flatMap(([field, errors]) => {
      const label = fieldLabels[field.toLowerCase()] ||
        field.replace(/[_-]+/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
      const messages = Array.isArray(errors) ? errors : [errors];
      return messages.flatMap((item) => {
        const error = formatErrorMessage(item, "");
        if (!error) return [];
        const normalizedError = error.replace(
          new RegExp(`\\b${field.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "gi"),
          label
        );
        return [normalizedError === error ? `${label}: ${error}` : normalizedError];
      });
    });
    return lines.join("\n") || fallback;
  }
  return fallback;
};

const AddCompany = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company_name: "",
    company_address: "",
    company_email: "",
    company_phone: "",
    bvn: "",
    industry: "",
    country: "",
    state: "",
    business_start_date: "",
  });
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loadingIndustries, setLoadingIndustries] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const fetchPlans = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${BASE_URL}/v1/get-all-plans`, { headers: getAuthHeaders() });
      const data = res?.data?.data || [];
      setPlans(data);
    } catch (err) {
      console.error("Failed to fetch plans", err);
      setError("Unable to load plans at the moment.");
      setPlans([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    const fetchIndustries = async () => {
      setLoadingIndustries(true);
      try {
        const res = await axios.get(`${BASE_URL}/v1/industry`, { headers: getAuthHeaders() });
        const data = Array.isArray(res?.data?.data) ? res.data.data : [];
        setIndustries(data);
      } catch (err) {
        console.error("Failed to fetch industries", err);
        setIndustries([]);
      } finally {
        setLoadingIndustries(false);
      }
    };

    fetchIndustries();
  }, []);

  useEffect(() => {
    // do not auto-select a plan; user must choose one (field is required)
    setSelectedPlan(null);
  }, [plans]);

  // close dropdown on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const validate = () => {
    const required = [
      "company_name",
      "company_address",
      "company_email",
      "company_phone",
      "bvn",
      "industry",
      "country",
      "state",
      "business_start_date",
    ];
    const missing = required.filter((k) => !String(formData[k] || "").trim());
    // plan selection is required
    if (!selectedPlan) missing.push("plan");
    return missing;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = validate();
    if (missing.length) {
      return Swal.fire({
        icon: "error",
        title: "Missing fields",
        text: `Please fill: ${missing.join(", ")}`,
        confirmButtonColor: "#16a34a",
      });
    }

    setSaving(true);
    try {
      const mainCompanyId = localStorage.getItem("activeCompanyId") || localStorage.getItem("company_id");
      // include plan_id in the payload as the API requires it
      const payload = {
        ...formData,
        plan_id: selectedPlan?.id || selectedPlan?.plan_id || selectedPlan?.planId || null,
      };

      const res = await axios.post(`${BASE_URL}/v1/create_additional_company`, payload, {
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      });

      const ok = res?.data?.status === true || res?.data?.success === true;
      if (ok) {
        const respData = res?.data || {};
        const createdData = respData?.data || {};
        const created = createdData?.company || respData?.company || createdData;
        const companyId = created?.id || created?._id || created?.company_id || created?.companyId ||
          createdData?.company_id || respData?.company_id || null;

        if (!companyId) {
          await Swal.fire({
            icon: "success",
            title: "Company added",
            text: "The additional company was created, but its ID could not be found. You will remain in the main account.",
            confirmButtonColor: "#16a34a",
          });
          navigate("/dashboard");
          return;
        }

        localStorage.setItem("pendingAdditionalCompanySetup", "true");
        localStorage.setItem("pendingAdditionalCompanyId", String(companyId));
        localStorage.setItem("pendingAdditionalCompanyInfo", JSON.stringify(created));
        localStorage.setItem("pendingAdditionalCompanyParentId", String(mainCompanyId || ""));
        localStorage.setItem("pendingAdditionalCompanyIndustryId", String(formData.industry));
        navigate("/pricing-new-payment", {
          state: {
            company_id: companyId,
            industryId: formData.industry,
            selectedPlan,
            isAdditionalCompany: true,
            main_company_id: mainCompanyId,
          },
        });
      } else {
        const text = formatErrorMessage(res?.data?.message, "Could not create company.");
        await Swal.fire({ icon: "error", title: "Failed", text, confirmButtonColor: "#16a34a" });
      }
    } catch (err) {
      const serverMsg = err?.response?.data?.message || err?.message;
      const text = formatErrorMessage(serverMsg, "Could not create company.");
      await Swal.fire({ icon: "error", title: "Error", text, confirmButtonColor: "#16a34a" });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    window.confirm("Discard changes and go back?") && navigate(-1);
  };

  return (
    <>
      <HeaderNav />
      <div className={classes.container}>
        <div className={classes.formWrapper}>
          <div className={classes.header}>
            <h1>Add Company</h1>
            <p className={classes.subtitle}>Create an additional company under your account.</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off">
            <div className={classes.formGroup}>
              <label htmlFor="company_name">Company name</label>
              <input id="company_name" name="company_name" type="text" placeholder="Company name" required value={formData.company_name} onChange={handleChange} />
            </div>

            <div className={classes.formGroup}>
              <label htmlFor="company_address">Address</label>
              <textarea id="company_address" name="company_address" placeholder="Street, City, State, Country" value={formData.company_address} onChange={handleChange} />
            </div>

            <div className={classes.row}>
              <div className={`${classes.colFlex} ${classes.formGroup}`}>
                <label htmlFor="company_email">Email</label>
                <input id="company_email" name="company_email" type="email" placeholder="company@example.com" value={formData.company_email} onChange={handleChange} />
              </div>
              <div className={`${classes.colNarrow} ${classes.formGroup}`}>
                <label htmlFor="company_phone">Phone</label>
                <input id="company_phone" name="company_phone" type="text" placeholder="+234 800 000 0000" value={formData.company_phone} onChange={handleChange} />
              </div>
            </div>

            <div className={classes.formGroup}>
              <label htmlFor="bvn">BVN</label>
              <input id="bvn" name="bvn" type="text" placeholder="Enter Bank Verification Number" value={formData.bvn} onChange={handleChange} />
            </div>

            <div className={classes.row}>
              <div className={`${classes.colFlex} ${classes.formGroup}`}>
                <label htmlFor="industry">Industry</label>
                <select id="industry" name="industry" value={formData.industry} onChange={handleChange}>
                  <option value="">{loadingIndustries ? "Loading industries..." : "Select industry"}</option>
                  {industries.map((industry) => (
                    <option key={industry.id} value={industry.id}>{industry.name}</option>
                  ))}
                </select>
              </div>
              <div className={`${classes.colNarrow} ${classes.formGroup}`}>
                <label htmlFor="business_start_date">Business start date</label>
                <input id="business_start_date" name="business_start_date" type="date" value={formData.business_start_date} onChange={handleChange} />
              </div>
            </div>

            <div className={classes.row}>
              <div className={`${classes.colFlex} ${classes.formGroup}`}>
                <label htmlFor="country">Country</label>
                <select id="country" name="country" value={formData.country} onChange={handleChange}>
                  <option value="">Select country</option>
                  <option value="Nigeria">Nigeria</option>
                  <option value="United States">United States</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Ghana">Ghana</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className={`${classes.colNarrow} ${classes.formGroup}`}>
                <label htmlFor="state">State</label>
                <select id="state" name="state" value={formData.state} onChange={handleChange}>
                  <option value="">Select state</option>
                  {formData.country === 'Nigeria' && (
                    <>
                      <option value="Lagos">Lagos</option>
                      <option value="Federal Capital Territory">Federal Capital Territory</option>
                      <option value="Rivers">Rivers</option>
                      <option value="Oyo">Oyo</option>
                      <option value="Ogun">Ogun</option>
                    </>
                  )}
                  {formData.country === 'United States' && (
                    <>
                      <option value="California">California</option>
                      <option value="New York">New York</option>
                      <option value="Texas">Texas</option>
                    </>
                  )}
                  {formData.country === 'United Kingdom' && (
                    <>
                      <option value="England">England</option>
                      <option value="Scotland">Scotland</option>
                      <option value="Wales">Wales</option>
                    </>
                  )}
                  {formData.country === 'Ghana' && (
                    <>
                      <option value="Greater Accra">Greater Accra</option>
                      <option value="Ashanti">Ashanti</option>
                      <option value="Northern">Northern</option>
                    </>
                  )}
                </select>
              </div>
            </div>

            <div className={classes.formGroup} style={{ marginTop: 12 }} ref={dropdownRef}>
              <label>Choose plan (required)</label>
              <div style={{ position: "relative", marginTop: 6 }}>
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen((s) => !s)}
                  className={classes.dropdownToggle}
                  aria-required="true"
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "8px 12px",
                    border: "1px solid #e5e7eb",
                    borderRadius: 6,
                    background: "#fff",
                    boxSizing: "border-box",
                  }}
                >
                  {selectedPlan ? selectedPlan.name : "Select a plan"}
                  <span style={{ float: "right", opacity: 0.6 }}>{isDropdownOpen ? "▲" : "▼"}</span>
                </button>

                {isDropdownOpen && (
                  <div
                    className={classes.dropdownMenu}
                    style={{
                      position: "absolute",
                      zIndex: 40,
                      left: 0,
                      right: 0,
                      background: "#fff",
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                      maxHeight: 260,
                      overflow: "auto",
                      padding: 8,
                    }}
                  >
                    {isLoading ? (
                      <div style={{ padding: 8 }}>Loading plans...</div>
                    ) : plans.length === 0 ? (
                      <div style={{ padding: 8 }}>No plans available</div>
                    ) : (
                      plans.map((plan, idx) => (
                        <div key={plan.id || plan.name || idx} style={{ padding: 8, borderBottom: "1px solid #f3f4f6" }}>
                          <div
                            role="button"
                            onClick={() => {
                              setSelectedPlan(plan);
                              setIsDropdownOpen(false);
                            }}
                            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                          >
                            <div style={{ fontWeight: 600 }}>{plan.name || `Plan ${idx + 1}`}</div>
                            <div style={{ color: "#6b7280" }}>{plan.monthly ? `₦${parseFloat(plan.monthly).toLocaleString()}/mo` : "—"}</div>
                          </div>

                          <div style={{ marginTop: 6, fontSize: 13, color: "#374151" }}>
                            <ul style={{ margin: "4px 0", paddingLeft: 20, listStyleType: "disc", listStylePosition: "outside", display: "block" }}>
                              {plan.priviledges && plan.priviledges.length > 0 ? (
                                plan.priviledges.map((p, i) => (
                                  <li key={i} style={{ marginBottom: 6, display: "list-item", width: "100%" }}>{p}</li>
                                ))
                              ) : (
                                <li style={{ display: "list-item" }}>No listed privileges</li>
                              )}
                            </ul>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className={classes.actions}>
              <button type="button" onClick={handleCancel} className={`${classes.btn} ${classes.btnGhost}`}>Cancel</button>
              <button type="submit" className={`${classes.btn} ${classes.btnPrimary}`} disabled={saving}>{saving ? "Creating..." : "Create company"}</button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddCompany;
